<template>
    <div>
        <div class="kgr-row">
            {{ $t('Hier können diverse Sportarten vereinsspezifisch angelegt werden. Zur Verfügung stehen diese dann im Kursmodul und für die Übungsleiterlizenzen.') }}
        </div>
        <kgr-form-scaffold
            :with-back-button="false"
            :pending="pending"
            @primaryBtnClicked="save"
        >
            <kgr-loader v-if="loading" />
            <table
                v-else
                class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell--headline"></th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('club.options.sport') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Bearbeiten') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(sport, index) in sports"
                        :key="index"
                        class="kgr-table__row"
                    >
                        <td :style="'background-color:' + (sport.colour ? sport.colour : '') + '; width: 6px;'"></td>
                        <td
                            v-if="isMarkEdited(sport)"
                            class="kgr-table__cell"
                        >
                            <div class="kgr-inputGroup kgr-postfix kgr-postfix--top">
                                <div class="kgr-postfix__fixed">
                                    <input
                                        v-model="sport.colour"
                                        type="color"
                                        class="kgr-input"
                                        style="width: 40px;"
                                    />
                                </div>
                                <kgr-icon
                                    v-show="sport.colour"
                                    icon-class="delete"
                                    class="deleteColour"
                                    :title="tDeleteColour"
                                    @click="sport.colour = null"
                                />
                                <input
                                    v-model="sport.name"
                                    type="text"
                                    maxlength="255"
                                    class="kgr-input"
                                    placeholder="Name"
                                />
                            </div>
                            <div class="kgr-inputGroup__text kgr-inputGroup__text--error">
                                {{ showErrorMessage(index) }}
                            </div>
                        </td>
                        <td
                            v-else
                            class="kgr-table__cell"
                            :class="sport.isArchived ? 'kgr-color kgr-color--gray' : ''"
                        >
                            {{ sport.name }}
                        </td>
                        <td class="kgr-table__cell">
                            <span
                                class="kgr-hidden kgr-hidden--fadeInOnUnhidden"
                                :class="{ 'is-unhidden': !isMarked(sport) }"
                            >
                                <button
                                    v-if="sport.id"
                                    class="kgr-table__action"
                                    :title="$t('bearbeiten')"
                                    @click="markEdited(sport)"
                                >
                                    <kgr-icon-edit
                                        size="15"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- delete button active -->
                                <button
                                    v-if="sport.isDeletable"
                                    class="kgr-table__action"
                                    :title="sport.id ? $t('löschen') : null"
                                    @click="sport.id ? markForDeletion(sport) : removeSport(sport)"
                                >
                                    <kgr-icon-delete
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- delete button disabled -->
                                <button
                                    v-if="!sport.isDeletable"
                                    class="kgr-table__action"
                                    :title="sport.reasonForNotDeletable"
                                >
                                    <kgr-icon-delete
                                        class="kgr-icon--light"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- archive button active -->
                                <button
                                    v-if="sport.id && sport.isArchivable"
                                    class="kgr-table__action"
                                    :title="$t('archivieren')"
                                    @click="markForArchive(sport)"
                                >
                                    <kgr-icon
                                        icon-class="archive"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- archive button disabled -->
                                <button
                                    v-if="sport.id && !sport.isArchivable"
                                    class="kgr-table__action"
                                    :title="sport.reasonForNotArchivable"
                                >
                                    <kgr-icon
                                        class="kgr-icon--light"
                                        icon-class="archive"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- unarchive button active -->
                                <button
                                    v-if="sport.id && sport.isArchived"
                                    class="kgr-table__action"
                                    :title="$t('reaktivieren')"
                                    @click="markForUnarchive(sport)"
                                >
                                    <kgr-icon
                                        icon-class="unarchive"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- unarchive button disabled -->
                                <button
                                    v-if="sport.id && !sport.isArchived"
                                    class="kgr-table__action"
                                    :title="$t('Kann nicht reaktiviert werden, da es nicht archiviert ist.')"
                                >
                                    <kgr-icon
                                        class="kgr-icon--light"
                                        icon-class="unarchive"
                                        style="pointer-events: none"
                                    />
                                </button>
                            </span>
                            <a
                                v-if="isMarkedForDeletion(sport)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForDeletion(sport)"
                            >{{ $t('Löschen rückgängig') }}</a>
                            <a
                                v-if="isMarkedForArchive(sport)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForArchive(sport)"
                            >{{ $t('Archivieren rückgängig') }}</a>
                            <a
                                v-if="isMarkedForUnarchive(sport)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForUnarchive(sport)"
                            >{{ $t('Reaktivieren rückgängig') }}</a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="kgr-text kgr-text--rightAligned">
                <div
                    class="kgr-button kgr-button--accent"
                    @click="addSport()"
                >
                    {{ $t('Neue Sportart hinzufügen') }}
                </div>
            </div>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import Error from '../../js/model/Error';
import FormScaffold from '../../components/common/form/FormScaffold.vue';
import Icon from '../../components/common/icon/Icon.vue';
import IconDelete from '../../components/common/icon/IconDelete.vue';
import IconEdit from '../../components/common/icon/IconEdit.vue';
import Loading from '../../components/common/Loading.vue';
import Sport from '../../js/dto/offering/Sport';
import SportRepository from '../../js/repository/SportsRepository';

export default {
    name: 'SportsOverview',
    components: {
        'kgr-loader': Loading,
        'kgr-icon-edit': IconEdit,
        'kgr-icon-delete': IconDelete,
        'kgr-icon': Icon,
        'kgr-form-scaffold': FormScaffold,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    computed: {
        tDeleteColour() {
            return this.$t('Farbe löschen');
        },
    },
    data() {
        return {
            loading: false,
            pending: false,
            sports: [],
            markedForDeletion: [],
            markedForArchive: [],
            markedForUnarchive: [],
            edited: [],
            error: new Error(),
        };
    },
    mounted() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            SportRepository.findAllWithArchived(this.clubId)
                .then(sports => this.sports = sports)
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        save() {
            this.pending = true;
            SportRepository.bulkUpdate(this.clubId, this.sports)
                .then(() => {
                    this.load();
                    this.error = new Error();
                    this.$notify.success('saveChanges.success');
                })
                .catch(error => {
                    this.error = error;
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => {
                    this.pending = false;
                });
        },
        addSport() {
            const sport = Sport.create();
            this.sports.push(sport);
            this.markEdited(sport);
        },
        removeSport(sport) {
            this.sports = this.sports.filter(element => element !== sport);
        },
        markForDeletion(sport) {
            this.markedForDeletion.push(sport);
            sport.delete = true;
            sport.isMarked = true;
        },
        unmarkForDeletion(sport) {
            this.markedForDeletion = this.markedForDeletion.filter(item => item !== sport);
            sport.delete = false;
            sport.isMarked = false;
        },
        markForArchive(sport) {
            this.markedForArchive.push(sport);
            sport.isArchived = true;
            sport.isMarked = true;
        },
        unmarkForArchive(sport) {
            this.markedForArchive = this.markedForArchive.filter(item => item !== sport);
            sport.isArchived = false;
            sport.isMarked = false;
        },
        markForUnarchive(sport) {
            this.markedForUnarchive.push(sport);
            sport.isArchived = false;
            sport.isMarked = true;
        },
        unmarkForUnarchive(sport) {
            this.markedForUnarchive = this.markedForUnarchive.filter(item => item !== sport);
            sport.isArchived = true;
            sport.isMarked = false;
        },
        markEdited(sport) {
            this.edited.push(sport);
        },
        isMarkEdited(category) {
            return this.edited.indexOf(category) !== -1;
        },
        isMarkedForDeletion(sport) {
            return this.markedForDeletion.indexOf(sport) !== -1;
        },
        isMarkedForArchive(sport) {
            return this.markedForArchive.indexOf(sport) !== -1;
        },
        isMarkedForUnarchive(sport) {
            return this.markedForUnarchive.indexOf(sport) !== -1;
        },
        isMarked(sport) {
            return this.isMarkedForDeletion(sport) ||
                this.isMarkedForArchive(sport) ||
                this.isMarkedForUnarchive(sport);
        },
        showErrorMessage(index) {
            return this.error.messageForField('sports[' + index + '][name]');
        }
    }
}
</script>

<style scoped>
.deleteColour {
    cursor: pointer;
    position: relative;
    top: 10px;
    margin: 5px;
}
</style>
